import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquareText, ChevronRight, Zap, HardHat } from 'lucide-react';

const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

// Encart "Nouveaux messages sur vos dossiers" — pour tous les utilisateurs.
// Lit la synthèse déjà chargée par le contexte (aucune requête supplémentaire = zéro ralentissement).
export default function WallUnreadWidget() {
    const navigate = useNavigate();
    const { wallUnreadItems, wallUnreadMessages } = useAuth();

    if (!wallUnreadItems || wallUnreadItems.length === 0) return null;

    return (
        <Card data-testid="wall-unread-widget" className="border-purple-200 bg-gradient-to-br from-purple-50/60 to-white">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquareText className="w-5 h-5 text-purple-600" />
                    Nouveaux messages sur vos dossiers
                    <Badge className="ml-1 bg-purple-600 text-white">{wallUnreadMessages}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                    {wallUnreadItems.map((it) => {
                        const isCh = it.module === 'chantier';
                        const Icon = isCh ? HardHat : Zap;
                        return (
                            <button
                                key={`${it.prospect_id}-${it.module}`}
                                type="button"
                                onClick={() => navigate(`/prospects/${it.prospect_id}?wall=${it.module}`)}
                                data-testid={`wall-unread-item-${it.prospect_id}-${it.module}`}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-50/70 transition-colors"
                            >
                                <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${isCh ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-semibold text-slate-800 truncate">{it.prospect_name}</p>
                                        <Badge variant="outline" className="text-[10px] shrink-0">{it.module_label}</Badge>
                                    </div>
                                    <p className="text-xs text-slate-500 truncate">
                                        {it.last_author_name ? `${it.last_author_name} · ` : ''}{timeAgo(it.last_message_at)}
                                    </p>
                                </div>
                                <Badge className="bg-purple-600 text-white shrink-0">{it.unread_count}</Badge>
                                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                            </button>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}
